import React, { useMemo } from "react";
import { SwapOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Row } from "antd";
import { connect } from "dva";
import { DealerExchangeType, DealerExchangeTypeMap } from "@/utils/enum";
import { childShipmentExchangeOfferIsValid, parentShipmentExchangeOfferIsValid } from "@/utils/exchangeShipment";
import { exchangeTypeAndToolTip } from "@/components/DealerExchangeAlert";

const ExchangeOfferAlert = ({ configPerm, shipmentData, handleClickYesExchange, handleClickNoExchange }) => {
  const { etasValidated, childShipmentId } = shipmentData;

  const { mainLegPosition, otherLegPosition } = useMemo(() => {
    return {
      mainLegPosition: childShipmentId ? "first" : "second",
      otherLegPosition: childShipmentId ? "second" : "first",
    };
  }, [childShipmentId]);

  const displayExchangeShipmentOffer = useMemo(() => {
    if (etasValidated) {
      return (
        childShipmentExchangeOfferIsValid(shipmentData, configPerm) ||
        parentShipmentExchangeOfferIsValid(shipmentData, configPerm)
      );
    }

    return false;
  }, [etasValidated, shipmentData, configPerm]);

  const message = useMemo(() => {
    const { isExchangeRequired, dealerExchangeType } = shipmentData ?? {};
    const requirementText = isExchangeRequired ? "and required" : "";
    return (
      <div>
        This shipment is the {mainLegPosition} leg of an{" "}
        <strong>{`${DealerExchangeTypeMap[dealerExchangeType]}`}</strong>
        {exchangeTypeAndToolTip(dealerExchangeType)} {requirementText} dealer exchange.
      </div>
    );
  }, [mainLegPosition, shipmentData]);

  const description = useMemo(() => {
    const { isExchangeRequired, dealerExchangeType } = shipmentData ?? {};
    const text = isExchangeRequired
      ? `This shipment has a dealer exchange requirement, and by accepting this shipment, you are agreeing to accept the ${otherLegPosition} leg as well`
      : `Would you be interested in dispatching the ${otherLegPosition} leg, as well?`;
    const orderedShipmentWarning =
      dealerExchangeType === DealerExchangeType.Ordered
        ? " Please note that the second leg will not be available for pickup until the first leg is delivered."
        : "";

    const okayButtonText = isExchangeRequired ? "Okay" : "Yes, I am interested";

    return (
      <>
        {text}
        <div>{orderedShipmentWarning}</div>
        <Row style={{ marginTop: 16 }} gutter={16}>
          <Col flex={1}>
            <Button block size="small" type="primary" onClick={handleClickYesExchange}>
              {okayButtonText}
            </Button>
          </Col>

          {!isExchangeRequired && (
            <Col flex={1}>
              <Button block size="small" type="primary" danger onClick={handleClickNoExchange}>
                No, I am not interested
              </Button>
            </Col>
          )}
        </Row>
      </>
    );
  }, [shipmentData, otherLegPosition, handleClickYesExchange, handleClickNoExchange]);

  if (!displayExchangeShipmentOffer) {
    return null;
  }

  return (
    <Alert
      type="info"
      showIcon
      icon={<SwapOutlined />}
      style={{ width: "100%", marginTop: 16, marginBottom: 16 }}
      message={message}
      description={description}
    />
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(ExchangeOfferAlert);
